import { tenantApi as api } from '../config/api'
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    downloadCertificatesExportJwt: build.query<
      DownloadCertificatesExportJwtApiResponse,
      DownloadCertificatesExportJwtApiArg
    >({
      query: (queryArg) => ({ url: `/certificates/export/download`, params: { token: queryArg.token } }),
    }),
    requestCertificatesExport: build.mutation<RequestCertificatesExportApiResponse, RequestCertificatesExportApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/certificates/export`,
        method: 'POST',
        body: queryArg.requestCertificatesExportInDto,
      }),
    }),
    downloadCertificate: build.query<DownloadCertificateApiResponse, DownloadCertificateApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/certificates/${queryArg.certificateId}/download` }),
    }),
    regenerateCertificate: build.mutation<RegenerateCertificateApiResponse, RegenerateCertificateApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/certificates/${queryArg.certificateId}/regenerate`,
        method: 'POST',
      }),
    }),
    getCourseZippedCertificates: build.query<GetCourseZippedCertificatesApiResponse, GetCourseZippedCertificatesApiArg>(
      {
        query: (queryArg) => ({
          url: `/tenants/${queryArg.tenantId}/courses/${queryArg.courseCode}/cycles/${queryArg.cycleId}/zipped-certificates`,
        }),
      },
    ),
    getCurrentUserCertificates: build.query<GetCurrentUserCertificatesApiResponse, GetCurrentUserCertificatesApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/me/certificates` }),
    }),
    downloadMyCertificate: build.query<DownloadMyCertificateApiResponse, DownloadMyCertificateApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/me/certificates/${queryArg.completionId}/download` }),
    }),
  }),
  overrideExisting: true,
})
export { injectedRtkApi as enhancedApi }
export type DownloadCertificatesExportJwtApiResponse = /** status 200 Successful Response */ null
export type DownloadCertificatesExportJwtApiArg = {
  /** JWT from the ready-email; carries the zip's identity and the link's real expiry. */
  token: string
}
export type RequestCertificatesExportApiResponse = /** status 202 Successful Response */ CertificatesExportAcceptedDto
export type RequestCertificatesExportApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  requestCertificatesExportInDto: RequestCertificatesExportInDto
}
export type DownloadCertificateApiResponse = /** status 200 Successful Response */ null
export type DownloadCertificateApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  certificateId: string
}
export type RegenerateCertificateApiResponse = /** status 200 Successful Response */ null
export type RegenerateCertificateApiArg = {
  tenantId: string
  certificateId: string
}
export type GetCourseZippedCertificatesApiResponse = /** status 200 Successful Response */ DownloadCertificateDto
export type GetCourseZippedCertificatesApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  /** Stable course business key; paths address courses by code, not id. */
  courseCode: string
  /** TrainingCycleDto.id. */
  cycleId: string
}
export type GetCurrentUserCertificatesApiResponse = /** status 200 Successful Response */ CertificateDto[]
export type GetCurrentUserCertificatesApiArg = {
  tenantId: string
}
export type DownloadMyCertificateApiResponse = unknown
export type DownloadMyCertificateApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  completionId: string
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type CertificatesExportAcceptedDto = {
  /** Downloadable certificates the selection holds. */
  certificates_found: number
  /** Whether a zip build was queued. The zip is emailed to the requester as a time-limited download link when it is ready. */
  dispatched: boolean
  /** Selected learners holding at least one of them. */
  users_with_certificates: number
}
export type RequestCertificatesExportInDto = {
  /** Learners whose earned certificates are exported as one zip. Selection from the Archive surface, so archived learners are the expected case, not an error. */
  user_ids: string[]
}
export type DownloadCertificateDto = {
  url: string
}
export type CertificateDto = {
  course_title: string
  id: string
  manually_regenerated_at?: string
  obtained_on: string
  training_code: string
  training_internal_name: string
  url?: string
}
export const {
  useDownloadCertificatesExportJwtQuery,
  useRequestCertificatesExportMutation,
  useDownloadCertificateQuery,
  useRegenerateCertificateMutation,
  useGetCourseZippedCertificatesQuery,
  useGetCurrentUserCertificatesQuery,
  useDownloadMyCertificateQuery,
} = injectedRtkApi
